import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import './App.css';

// Layouts
const DashboardLayout = lazy(() => import('@/components/layout/DashboardLayout'));
const AdminLayout = lazy(() => import('@/components/layout/AdminLayout'));
const LandingLayout = lazy(() => import('@/components/layout/LandingLayout'));

// Auth Pages
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));

// Admin Pages
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const AdminSettingsPage = lazy(() => import('@/pages/AdminSettingsPage'));
const AdminSupportPage = lazy(() => import('@/pages/AdminSupportPage'));
const AdminChatPage = lazy(() => import('@/pages/AdminChatPage'));
const AdminGrowthPage = lazy(() => import('@/pages/AdminGrowthPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));

// Dashboard Pages
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const OrdersPage = lazy(() => import('@/pages/OrdersPage'));
const CustomersPage = lazy(() => import('@/pages/CustomersPage'));
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'));

// New Internal Pages
const MultiStoresPage = lazy(() => import('@/pages/MultiStoresPage'));
const CouponsPage = lazy(() => import('@/pages/CouponsPage'));
const SubscriptionsPage = lazy(() => import('@/pages/SubscriptionsPage'));

// Store Customization Pages
const StoreCustomizationPage = lazy(() => import('@/pages/StoreCustomizationPage'));
const StoreVisualEditor = lazy(() => import('@/pages/StoreVisualEditor'));
const MerchantSubscriptionsPage = lazy(() => import('@/pages/MerchantSubscriptionsPage'));
const BillingSuccessPage = lazy(() => import('@/pages/BillingSuccessPage'));

// Other Pages
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const SupportPage = lazy(() => import('@/pages/SupportPage'));
const ImportProductPage = lazy(() => import('@/pages/ImportProductPage'));

// Restored Pages
const MarketingPage = lazy(() => import('@/pages/MarketingPage'));
const CommissionsPage = lazy(() => import('@/pages/CommissionsPage'));
const ActivityLogPage = lazy(() => import('@/pages/ActivityLogPage'));
const BackupPage = lazy(() => import('@/pages/BackupPage'));
const ThemesPage = lazy(() => import('@/pages/ThemesPage'));
const ThemeMarketPage = lazy(() => import('@/pages/ThemeMarketPage'));
const PagesPage = lazy(() => import('@/pages/PagesPage'));
const DomainPage = lazy(() => import('@/pages/DomainPage'));
const VisualIdentityPage = lazy(() => import('@/pages/VisualIdentityPage'));
const LandingBuilderPage = lazy(() => import('@/pages/LandingBuilderPage'));
const LandingViewPage = lazy(() => import('@/pages/LandingViewPage'));
const WalletPage = lazy(() => import('@/pages/WalletPage'));
const AffiliatePage = lazy(() => import('@/pages/AffiliatePage'));
const ShippingPage = lazy(() => import('@/pages/ShippingPage'));

// Landing Pages (Footer Pages)
const LandingPage = lazy(() => import('@/pages/LandingPage'));
// ... other landing pages

// Store Pages
const StoreLayout = lazy(() => import('@/components/layout/StoreLayout'));
const StoreLandingPage = lazy(() => import('@/pages/store/StoreLandingPage'));
const StoreProductsPage = lazy(() => import('@/pages/store/StoreProductsPage'));
const StoreProductDetailPage = lazy(() => import('@/pages/store/StoreProductDetailPage'));
const StoreAuthPage = lazy(() => import('@/pages/store/StoreAuthPage'));
const StoreCartPage = lazy(() => import('@/pages/store/StoreCartPage'));
const StoreCheckoutPage = lazy(() => import('@/pages/store/StoreCheckoutPage'));
const StoreOrdersPage = lazy(() => import('@/pages/store/StoreOrdersPage'));
const StoreProfilePage = lazy(() => import('@/pages/store/StoreProfilePage'));
const StoreAboutPage = lazy(() => import('@/pages/store/StoreAboutPage'));
const StoreContactPage = lazy(() => import('@/pages/store/StoreContactPage'));
const StoreAffiliatePage = lazy(() => import('@/pages/store/StoreAffiliatePage'));
const StoreCustomPage = lazy(() => import('@/pages/store/StoreCustomPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AboutPage = lazy(() => import('@/pages/landing/AboutPage'));
const FeaturesPage = lazy(() => import('@/pages/landing/FeaturesPage'));
const PricingPage = lazy(() => import('@/pages/landing/PricingPage'));
const BlogPage = lazy(() => import('@/pages/landing/BlogPage'));
const JobsPage = lazy(() => import('@/pages/landing/JobsPage'));
const HelpCenterPage = lazy(() => import('@/pages/landing/HelpCenterPage'));
const DocumentationPage = lazy(() => import('@/pages/landing/DocumentationPage'));
const ApiPage = lazy(() => import('@/pages/landing/ApiPage'));
const PartnersPage = lazy(() => import('@/pages/landing/PartnersPage'));
const MarketersPage = lazy(() => import('@/pages/landing/MarketersPage'));
const TermsPage = lazy(() => import('@/pages/landing/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/landing/PrivacyPage'));
const FaqPage = lazy(() => import('@/pages/landing/FaqPage'));
const RefundPage = lazy(() => import('@/pages/landing/RefundPage'));
const CommercialTermsPage = lazy(() => import('@/pages/landing/CommercialTermsPage'));
const VideoPage = lazy(() => import('@/pages/landing/VideoPage'));

// Context
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeEditorProvider } from '@/contexts/ThemeEditorContext';
import { PlatformSettingsProvider } from '@/contexts/PlatformSettingsContext';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import PlatformLogo from '@/components/shared/PlatformLogo';
import { PlatformRouteSEO } from '@/components/seo/PlatformRouteSEO';

const FEATURE_PATHS = [
  { path: '/import-product', features: ['product_imports'] },
  { path: '/marketing', features: ['automation'] },
  { path: '/commissions', features: ['affiliate'] },
  { path: '/affiliate', features: ['affiliate'] },
  { path: '/domain', features: ['custom_domain'] },
  { path: '/reports', features: ['reports', 'advanced_reports'] },
  { path: '/theme-market', features: ['premium_themes'] },
  { path: '/themes', features: ['basic_themes', 'premium_themes'] },
  { path: '/activity-log', features: ['activity_logs'] },
  { path: '/backup', features: ['backups'] },
  { path: '/pages', features: ['pages'] },
  { path: '/visual-identity', features: ['visual_identity'] },
  { path: '/landing-builder', features: ['landing_pages'] },
  { path: '/wallet', features: ['wallet'] },
];

function MerchantDashboardGate() {
  const { currentSubscription, isSubscriptionLoading } = useAuth();
  const location = useLocation();

  if (isSubscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <PlatformLogo className="h-24 w-auto max-w-[160px] object-contain mx-auto mb-6" />
          <Loader2 className="w-12 h-12 animate-spin text-[#0d9488] mx-auto" />
        </div>
      </div>
    );
  }

  if (!currentSubscription && location.pathname !== '/subscriptions') {
    return <Navigate to="/subscriptions" replace />;
  }

  const requiredFeature = FEATURE_PATHS.find(
    ({ path }) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  const planFeatures: string[] = currentSubscription?.plan?.features || [];

  if (requiredFeature && !requiredFeature.features.some((feature) => planFeatures.includes(feature))) {
    return <Navigate to="/subscriptions" replace />;
  }

  return <DashboardLayout />;
}

function AppRoutes() {
  const { 
    isAdminAuthenticated, 
    adminUser, 
    isCustomerAuthenticated,
    customerUser,
    isLoading 
  } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <PlatformLogo className="h-24 w-auto max-w-[160px] object-contain mx-auto mb-6" />
          <Loader2 className="w-12 h-12 animate-spin text-[#0d9488] mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <Router>
      <PlatformRouteSEO />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#0d9488]" aria-label="Loading" /></div>}>
      <Routes>
        <Route
          path="/auth/callback"
          element={<AuthCallbackPage />}
        />

        <Route path="/verify-email" element={<VerifyEmailPage />} />

        <Route
          path="/billing/success"
          element={isAdminAuthenticated ? <BillingSuccessPage /> : <Navigate to="/auth" />}
        />

        <Route
          path="/auth"
          element={
            isAdminAuthenticated ? (
              adminUser?.role === 'admin' ? <Navigate to="/admin/dashboard" /> : <Navigate to="/dashboard" />
            ) : <AuthPage />
          }
        />

        <Route
          path="/"
          element={
            isAdminAuthenticated ? (
              adminUser?.role === 'admin' ? <Navigate to="/admin/dashboard" /> : <Navigate to="/dashboard" />
            ) : isCustomerAuthenticated ? (
               <Navigate to="/landing" /> // Keep customers on landing for now
            ) : <Navigate to="/landing" />
          }
        />

        {/* Admin Routes (Protected) */}
        <Route element={isAdminAuthenticated && adminUser?.role === 'admin' ? <AdminLayout /> : <Navigate to="/auth" />}>
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/stores" element={<MultiStoresPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/admin/support" element={<AdminSupportPage />} />
          <Route path="/admin/chat" element={<AdminChatPage />} />
          <Route path="/admin/growth" element={<AdminGrowthPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
          <Route path="/admin/notifications" element={<NotificationsPage />} />
          <Route path="/admin/profile" element={<ProfilePage />} />
          <Route path="/admin/activity-log" element={<ActivityLogPage />} />
          <Route path="/admin/backup" element={<BackupPage />} />
          <Route path="/admin/wallet" element={<WalletPage />} />
        </Route>

        {/* Merchant Dashboard Routes (Protected) */}
        <Route element={isAdminAuthenticated && adminUser?.role === 'merchant' ? <MerchantDashboardGate /> : <Navigate to="/auth" />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/coupons" element={<CouponsPage />} />
          <Route path="/store-customization" element={<StoreCustomizationPage />} />
          <Route path="/store-editor" element={<StoreVisualEditor />} />
          <Route path="/subscriptions" element={<MerchantSubscriptionsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/import-product" element={<ImportProductPage />} />
          
          {/* Restored Routes */}
          <Route path="/marketing" element={<MarketingPage />} />
          <Route path="/commissions" element={<CommissionsPage />} />
          <Route path="/activity-log" element={<ActivityLogPage />} />
          <Route path="/backup" element={<BackupPage />} />
          <Route path="/themes" element={<ThemesPage />} />
          <Route path="/theme-market" element={<ThemeMarketPage />} />
          <Route path="/pages" element={<PagesPage />} />
          <Route path="/domain" element={<DomainPage />} />
          <Route path="/visual-identity" element={<VisualIdentityPage />} />
          <Route path="/landing-builder" element={<LandingBuilderPage />} />
          <Route path="/landing-builder/:id" element={<LandingBuilderPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/shipping" element={<ShippingPage />} />
        </Route>

        {/* Landing/Public Routes */}
        <Route element={<LandingLayout />}>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/docs" element={<DocumentationPage />} />
          <Route path="/api" element={<ApiPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/marketers" element={<MarketersPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/commercial-terms" element={<CommercialTermsPage />} />
          <Route path="/videos" element={<VideoPage />} />
        </Route>

        {/* Store landing pages render inside their owning store's layout. */}
        <Route path="/landing/:slug" element={<LandingViewPage />} />

        {/* Public Storefront Routes */}
        <Route path="/store/:slug" element={<StoreLayout />}>
          <Route index element={<StoreLandingPage />} />
          <Route path="products" element={<StoreProductsPage />} />
          <Route path="product/:productId" element={<StoreProductDetailPage />} />
          <Route path="auth" element={<StoreAuthPage />} />
          <Route path="cart" element={<StoreCartPage />} />
          <Route path="checkout" element={<StoreCheckoutPage />} />
          <Route path="orders" element={<StoreOrdersPage />} />
          <Route path="profile" element={<StoreProfilePage />} />
          <Route path="about" element={<StoreAboutPage />} />
          <Route path="contact" element={<StoreContactPage />} />
          <Route path="affiliate" element={<StoreAffiliatePage />} />
          <Route path="page/:pageSlug" element={<StoreCustomPage />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Route>

        {/* 404 Catch-All */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <PlatformSettingsProvider>
        <ThemeEditorProvider>
          <AppRoutes />
          <Toaster position="bottom-center" richColors />
        </ThemeEditorProvider>
      </PlatformSettingsProvider>
    </AuthProvider>
  );
}

export default App;
